import React, { useMemo, useState } from 'react';
import { ClockIcon, DocumentArrowUpIcon, DocumentIcon, TrashIcon } from '@heroicons/react/24/outline';
import { DocumentType, OfferDocument, ProductDocument } from '../../services/api';
import {
  flattenProductDocuments,
  getProductDocumentParentOptions,
} from '../../utils/productDocuments';

export interface PendingDocumentUpload {
  clientId: string;
  file: File;
  fileType: DocumentType;
  fileName: string;
  parentDocumentId?: number;
  parentClientId?: string;
}

interface DocumentUploadProps {
  documents: (OfferDocument | ProductDocument)[];
  pendingDocuments?: PendingDocumentUpload[];
  onUpload: (
    file: File,
    fileType: DocumentType,
    fileName: string,
    parentDocumentId?: number,
    parentClientId?: string,
  ) => Promise<void>;
  onDelete: (documentId: number) => Promise<void>;
  onDeletePending?: (clientId: string) => void;
  disabled?: boolean;
  supportsChildDocuments?: boolean;
}

interface PendingDocumentNode extends PendingDocumentUpload {
  childDocuments: PendingDocumentNode[];
}

const buildPendingTree = (documents: PendingDocumentUpload[]) => {
  const byId = new Map<string, PendingDocumentNode>(
    documents.map((document) => [
      document.clientId,
      {
        ...document,
        childDocuments: [],
      },
    ]),
  );

  const roots: PendingDocumentNode[] = [];

  for (const document of documents) {
    const current = byId.get(document.clientId);
    if (!current) continue;

    if (document.parentClientId && byId.has(document.parentClientId)) {
      byId.get(document.parentClientId)?.childDocuments.push(current);
    } else {
      roots.push(current);
    }
  }

  return roots;
};

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  documents,
  pendingDocuments = [],
  onUpload,
  onDelete,
  onDeletePending,
  disabled,
  supportsChildDocuments = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<DocumentType>('OTHER');
  const [fileName, setFileName] = useState('');
  const [parentSelection, setParentSelection] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deletePendingConfirm, setDeletePendingConfirm] = useState<string | null>(null);

  const documentTypes: DocumentType[] = [
    'SPECIFICATION',
    'CERTIFICATE',
    'DATASHEET',
    'INVOICE',
    'CONTRACT',
    'IMAGE',
    'COA',
    'MSDS',
    'OTHER',
  ];

  const productDocuments = documents as ProductDocument[];
  const flatDocuments = useMemo(
    () => (supportsChildDocuments ? flattenProductDocuments(productDocuments) : []),
    [productDocuments, supportsChildDocuments],
  );
  const pendingTree = useMemo(
    () => buildPendingTree(pendingDocuments),
    [pendingDocuments],
  );
  const uploadedParentOptions = useMemo(
    () => (supportsChildDocuments ? getProductDocumentParentOptions(productDocuments) : []),
    [productDocuments, supportsChildDocuments],
  );
  const pendingParentOptions = useMemo(
    () => {
      const walk = (nodes: PendingDocumentNode[], depth = 0): Array<{ value: string; label: string }> =>
        nodes.flatMap((node) => [
          {
            value: `pending:${node.clientId}`,
            label: `${depth > 0 ? `${'  '.repeat(depth)}- ` : ''}${node.fileName}`,
          },
          ...walk(node.childDocuments, depth + 1),
        ]);

      return walk(pendingTree);
    },
    [pendingTree],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const parentDocumentId = parentSelection.startsWith('uploaded:')
      ? Number(parentSelection.split(':')[1])
      : undefined;
    const parentClientId = parentSelection.startsWith('pending:')
      ? parentSelection.split(':')[1]
      : undefined;

    setUploading(true);
    try {
      await onUpload(
        selectedFile,
        fileType,
        fileName,
        parentDocumentId,
        parentClientId,
      );
      setSelectedFile(null);
      setFileName('');
      setFileType('OTHER');
      setParentSelection('');
      const fileInput = document.getElementById('file-upload') as HTMLInputElement | null;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId: number) => {
    try {
      await onDelete(documentId);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileTypeColor = (type: DocumentType): string => {
    const colors: Record<DocumentType, string> = {
      SPECIFICATION: 'bg-primary-100 text-primary-800',
      CERTIFICATE: 'bg-success-100 text-success-800',
      DATASHEET: 'bg-secondary-100 text-secondary-800',
      INVOICE: 'bg-warning-100 text-warning-800',
      PURCHASE_ORDER: 'bg-primary-100 text-primary-800',
      CONTRACT: 'bg-error-100 text-error-800',
      IMAGE: 'bg-secondary-100 text-secondary-800',
      COA: 'bg-success-100 text-success-800',
      MSDS: 'bg-warning-100 text-warning-800',
      OTHER: 'bg-neutral-100 text-neutral-800',
    };
    return colors[type] || colors.OTHER;
  };

  const renderProductDocuments = (items: ProductDocument[], depth = 0): React.ReactNode =>
    items.map((doc) => (
      <div key={doc.id} className="space-y-2">
        <div
          className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 p-3 transition-colors hover:bg-neutral-100"
          style={{ marginLeft: `${depth * 20}px` }}
        >
          <div className="flex min-w-0 flex-1 items-center space-x-3">
            <DocumentIcon className="h-5 w-5 flex-shrink-0 text-neutral-400" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-neutral-900">{doc.fileName}</p>
              <div className="mt-1 flex items-center space-x-2">
                <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${getFileTypeColor(doc.fileType)}`}>
                  {doc.fileType}
                </span>
                <span className="text-xs text-neutral-500">{formatFileSize(doc.fileSize)}</span>
                <span className="text-xs text-neutral-500">
                  {new Date(doc.uploadedAt).toLocaleDateString()}
                </span>
                {depth > 0 && (
                  <span className="inline-flex items-center rounded bg-cyan-50 px-2 py-0.5 text-xs font-medium text-cyan-700">
                    Child document
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="ml-4 flex items-center space-x-2">
            {deleteConfirm === doc.id ? (
              <>
                <button
                  type="button"
                  onClick={() => handleDelete(doc.id)}
                  className="rounded bg-error-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-error-700"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="rounded bg-neutral-200 px-3 py-1 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-300"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setDeleteConfirm(doc.id)}
                disabled={disabled}
                className="rounded-lg p-2 text-error-600 transition-colors hover:bg-error-50 disabled:opacity-50"
                title="Delete document"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {doc.childDocuments && doc.childDocuments.length > 0 && renderProductDocuments(doc.childDocuments, depth + 1)}
      </div>
    ));

  const renderPendingDocuments = (items: PendingDocumentNode[], depth = 0): React.ReactNode =>
    items.map((doc) => (
      <div key={doc.clientId} className="space-y-2">
        <div
          className="flex items-center justify-between rounded-lg border border-warning-200 bg-warning-50 p-3 transition-colors hover:bg-warning-100"
          style={{ marginLeft: `${depth * 20}px` }}
        >
          <div className="flex min-w-0 flex-1 items-center space-x-3">
            <DocumentIcon className="h-5 w-5 flex-shrink-0 text-warning-500" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-neutral-900">{doc.fileName}</p>
              <div className="mt-1 flex items-center space-x-2">
                <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${getFileTypeColor(doc.fileType)}`}>
                  {doc.fileType}
                </span>
                <span className="text-xs text-neutral-500">{formatFileSize(doc.file.size)}</span>
                <span className="inline-flex items-center rounded bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-800">
                  Pending
                </span>
                {depth > 0 && (
                  <span className="inline-flex items-center rounded bg-cyan-50 px-2 py-0.5 text-xs font-medium text-cyan-700">
                    Child document
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="ml-4 flex items-center space-x-2">
            {deletePendingConfirm === doc.clientId ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    onDeletePending?.(doc.clientId);
                    setDeletePendingConfirm(null);
                  }}
                  className="rounded bg-error-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-error-700"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  onClick={() => setDeletePendingConfirm(null)}
                  className="rounded bg-neutral-200 px-3 py-1 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-300"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setDeletePendingConfirm(doc.clientId)}
                disabled={disabled}
                className="rounded-lg p-2 text-error-600 transition-colors hover:bg-error-50 disabled:opacity-50"
                title="Remove pending document"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {doc.childDocuments.length > 0 && renderPendingDocuments(doc.childDocuments, depth + 1)}
      </div>
    ));

  return (
    <div className="space-y-4">
      <div className="rounded-lg border-2 border-dashed border-neutral-300 p-4">
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <DocumentArrowUpIcon className="h-5 w-5 text-neutral-400" />
            <span className="text-sm font-medium text-neutral-700">Upload Document</span>
          </div>

          <div className={`grid grid-cols-1 gap-3 ${supportsChildDocuments ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700">File</label>
              <input
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
                disabled={disabled || uploading}
                className="block w-full text-sm text-neutral-500 file:mr-4 file:rounded-lg file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary-700 hover:file:bg-primary-100 disabled:opacity-50"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700">Document Type</label>
              <select
                value={fileType}
                onChange={(e) => setFileType(e.target.value as DocumentType)}
                disabled={disabled || uploading}
                className="block w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 disabled:opacity-50"
              >
                {documentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-neutral-700">Display Name (Optional)</label>
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Custom file name"
                disabled={disabled || uploading}
                className="block w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 disabled:opacity-50"
              />
            </div>

            {supportsChildDocuments && (
              <div>
                <label className="mb-1 block text-xs font-medium text-neutral-700">Parent Document</label>
                <select
                  value={parentSelection}
                  onChange={(e) => setParentSelection(e.target.value)}
                  disabled={disabled || uploading || (flatDocuments.length === 0 && pendingDocuments.length === 0)}
                  className="block w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 disabled:opacity-50"
                >
                  <option value="">Top-level document</option>
                  {uploadedParentOptions.map((option) => (
                    <option key={`uploaded-${option.id}`} value={`uploaded:${option.id}`}>
                      {option.label}
                    </option>
                  ))}
                  {pendingParentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      Pending: {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {supportsChildDocuments && (
            <p className="text-xs text-neutral-500">
              Choose a parent document to upload this file as a child document, such as a child COA under a main COA.
            </p>
          )}

          <button
            type="button"
            onClick={handleUpload}
            disabled={!selectedFile || disabled || uploading}
            className="w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </button>
        </div>
      </div>

      {pendingDocuments.length > 0 && (
        <div className="space-y-2">
          <h4 className="flex items-center text-sm font-medium text-neutral-700">
            <ClockIcon className="mr-1 h-4 w-4 text-warning-500" />
            Pending Documents ({pendingDocuments.length}) - Will be uploaded when you save
          </h4>
          <div className="space-y-2">
            {supportsChildDocuments
              ? renderPendingDocuments(pendingTree)
              : pendingDocuments.map((doc) => (
                <div
                  key={doc.clientId}
                  className="flex items-center justify-between rounded-lg border border-warning-200 bg-warning-50 p-3 transition-colors hover:bg-warning-100"
                >
                  <div className="flex min-w-0 flex-1 items-center space-x-3">
                    <DocumentIcon className="h-5 w-5 flex-shrink-0 text-warning-500" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-neutral-900">{doc.fileName}</p>
                      <div className="mt-1 flex items-center space-x-2">
                        <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${getFileTypeColor(doc.fileType)}`}>
                          {doc.fileType}
                        </span>
                        <span className="text-xs text-neutral-500">{formatFileSize(doc.file.size)}</span>
                        <span className="inline-flex items-center rounded bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-800">
                          Pending
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="ml-4 flex items-center space-x-2">
                    {deletePendingConfirm === doc.clientId ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            onDeletePending?.(doc.clientId);
                            setDeletePendingConfirm(null);
                          }}
                          className="rounded bg-error-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-error-700"
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeletePendingConfirm(null)}
                          className="rounded bg-neutral-200 px-3 py-1 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-300"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDeletePendingConfirm(doc.clientId)}
                        disabled={disabled}
                        className="rounded-lg p-2 text-error-600 transition-colors hover:bg-error-50 disabled:opacity-50"
                        title="Remove pending document"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {documents.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-neutral-700">
            Uploaded Documents ({supportsChildDocuments ? flatDocuments.length : documents.length})
          </h4>
          <div className="space-y-2">
            {supportsChildDocuments
              ? renderProductDocuments(productDocuments)
              : documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between rounded-lg border border-neutral-200 bg-neutral-50 p-3 transition-colors hover:bg-neutral-100"
                >
                  <div className="flex min-w-0 flex-1 items-center space-x-3">
                    <DocumentIcon className="h-5 w-5 flex-shrink-0 text-neutral-400" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-neutral-900">{doc.fileName}</p>
                      <div className="mt-1 flex items-center space-x-2">
                        <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${getFileTypeColor(doc.fileType)}`}>
                          {doc.fileType}
                        </span>
                        <span className="text-xs text-neutral-500">{formatFileSize(doc.fileSize)}</span>
                        <span className="text-xs text-neutral-500">
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="ml-4 flex items-center space-x-2">
                    {deleteConfirm === doc.id ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleDelete(doc.id)}
                          className="rounded bg-error-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-error-700"
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(null)}
                          className="rounded bg-neutral-200 px-3 py-1 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-300"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(doc.id)}
                        disabled={disabled}
                        className="rounded-lg p-2 text-error-600 transition-colors hover:bg-error-50 disabled:opacity-50"
                        title="Delete document"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
